import { useQuery } from '@tanstack/react-query';
import { Alert, Badge, Spinner } from 'reactstrap';
import { PageHeader } from '../components/PageHeader';
import { OverviewGrid } from '../components/OverviewGrid';
import { nexusFetch } from '../nexus/nexusClient';

/**
 * Nexus-API · Übersicht.
 *
 * Landing-Page für den Nexus-Bereich: Live-Health der Nexus-API (über den
 * BFF-Proxy /proxy/nexus → /v1/healthz) plus Navigations-Karten zu allen
 * Nexus-Sub-Views. Ersetzt den bisherigen PlaceholderPage-Stub.
 */
function useNexusHealth() {
  return useQuery({
    queryKey: ['nexus-health'],
    queryFn: () => nexusFetch('/healthz'),
    refetchInterval: 30_000,
    retry: false,
  });
}

const CARDS = [
  { to: '/admin/settings/nexus/plugins', icon: '🧩', title: 'Plugin-Registry', status: 'live',
    description: 'Registrierte Provisioning-Plugins (Enhance, INWX, Proxmox …) mit Version + Status.' },
  { to: '/admin/settings/nexus/routing', icon: '🔀', title: 'Tenant-Routing', status: 'live',
    description: 'Pro Tenant welches Plugin/Backend eine Kategorie (Hosting, DNS, Mail) bedient.' },
  { to: '/admin/settings/nexus/settings', icon: '⚙️', title: 'System-Settings', status: 'live',
    description: 'App-Settings der Nexus-API — Feature-Flags, Limits, Integrations-Defaults.' },
  { to: '/admin/settings/nexus/jobs', icon: '🛠️', title: 'Jobs-Monitor', status: 'live',
    description: 'BullMQ-Provisioning-Jobs: Status, Retries, Payload-Redaction, Idempotency.' },
  { to: '/admin/settings/nexus/saga', icon: '↩️', title: 'Saga-Review', status: 'live',
    description: 'Compensation-Sagas — fehlgeschlagene Multi-Step-Provisionings + Rollback.' },
  { to: '/admin/settings/nexus/mail-security', icon: '🛡️', title: 'Mail-Security', status: 'live',
    description: 'SPF/DKIM/DMARC/BIMI-Provisioning + Diff-Preview pro Customer-Domain.' },
  { to: '/admin/settings/nexus/postmaster', icon: '📮', title: 'Postmaster SOC', status: 'live',
    description: 'RBL-Blacklist-Incidents + Delisting (Microsoft, Spamhaus, Barracuda).' },
  { to: '/admin/settings/nexus/usage', icon: '📊', title: 'Usage Explorer', status: 'live',
    description: 'Aggregierte Usage-Metriken pro Tenant/Service für Billing + Reporting.' },
  { to: '/admin/settings/nexus/customers', icon: '🏢', title: 'Customers', status: 'live',
    description: 'NetBox-gespiegelte Customer + Service-Mapping, Auto-Create-Status.' },
  { to: '/admin/settings/nexus/hosting-ops', icon: '🌐', title: 'Hosting-Operations', status: 'live',
    description: 'Webhosting-Sites, DNS-Zonen, Mailbox-CRUD gegen die Enhance-API.' },
  { to: '/admin/settings/nexus/dedicated', icon: '🖥️', title: 'Dedicated-Server', status: 'live',
    description: 'Dedizierte Server / VPS aus NetBox — Inventar + Zuordnung.' },
  { to: '/admin/settings/nexus/gateway-allowlist', icon: '🔐', title: 'CrowdSec-Allowlist', status: 'live',
    description: 'IP-Allowlist für die exponierte CrowdSec-API (cs.api.valitype.net).' },
];

export function NexusOverviewPage() {
  const health = useNexusHealth();

  return (
    <div>
      <PageHeader
        title="Nexus API · Übersicht"
        subtitle="Provisioning- & Plugin-Layer. Tenantless Admin-Fläche über /proxy/nexus (M2M)."
      />

      <Alert color={health.isError ? 'danger' : 'light'} className="d-flex align-items-center gap-2 small">
        <strong>API-Status:</strong>
        {health.isLoading ? (
          <><Spinner size="sm" /> prüfe …</>
        ) : health.isError ? (
          <Badge color="danger" pill>nicht erreichbar</Badge>
        ) : (
          <>
            <Badge color="success" pill>ok</Badge>
            <span className="text-muted">nexus · {health.data?.env ?? 'unbekannt'}</span>
          </>
        )}
      </Alert>

      <OverviewGrid cards={CARDS} />
    </div>
  );
}
